<template>
  <vs-popup
    class="popup"
    class-content="modal-key-activity"
    button-close-hidden
    v-bind="$attrs"
    v-on="$listeners"
  >
    <h3 class="vs-popup--title-on-top">
      {{ isAdd ? 'Add' : 'Edit' }} Key Activity
    </h3>
    <img
      class="vs-popup--close-button cursor-pointer"
      :src="require('@/assets/shipo-popup-icon-close.svg')"
      @click="closeModal"
      style="z-index: 2;"
    >
    <div
      id="modalContainer"
      class="w-full vs-con-loading__container grid grid-cols-1 gap-5 px-3 overflow-y-auto mt-12"
    >
      <hr class="border-dashed mb-2">
      <p class="text-purple-600 font-medium mb-2">
        General Information
      </p>
      <div class="grid grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium mb-1">Trip Status</label>
          <vs-input
            v-model="form.trip_status"
            class="w-full py-2"
            disabled
          />
        </div>
        <div>
          <label class="block text-sm font-medium mb-1">Key Activity Code</label>
          <vs-input
            v-model="form.code"
            class="w-full py-2"
            placeholder="Key Activity Code"
            :color="!form.code ? 'danger' : ''"
          />
        </div>
        <div>
          <label class="block text-sm font-medium mb-1">Key Activity Name (English)</label>
          <vs-input
            v-model="form.name"
            class="w-full py-2"
            placeholder="Key Activity Name (English)"
            :color="!form.name ? 'danger' : ''"
          />
        </div>
        <div>
          <label class="block text-sm font-medium mb-1">Key Activity Name (Indonesia)</label>
          <vs-input
            v-model="form.name_bahasa"
            class="w-full py-2"
            placeholder="Key Activity Name (Indonesia)"
            :color="!form.name_bahasa ? 'danger' : ''"
          />
        </div>
        <div>
          <label class="block text-sm font-medium mb-1">Visibility</label>
          <select-with-search
            v-model="form.visibility"
            placeholder="Select visibility"
            class="py-2"
            :options="visibilityOptions"
            :is-valid="!isEmpty(form.visibility)"
            hide-search
          />
        </div>
      </div>

      <!-- File Upload -->
      <div class="text-sm font-medium mb-1">
        Key Activity Icon
      </div>
      <div
        class="flex items-center justify-center border-2 border-dashed rounded-lg py-4 mb-3 cursor-pointer hover:bg-purple-50"
        :class="[iconActivity ? 'border-purple-700' : 'border-red-600']"
        @click="$refs.fileInput.click()"
      >
        <div class="text-center">
          <div class="text-gray-500 text-sm font-medium">
            <div class="flex flex-row items-center justify-start pr-2 w-full">
              <img
                :src="require('@/assets/shipo-icon-edit-upload-primary.svg')"
                class="flex-shrink-0 mr-2 h-6"
              >
              <div class="truncate">
                Upload File
              </div>
            </div>
          </div>
          <input
            ref="fileInput"
            type="file"
            class="hidden"
            accept="image/png, image/svg+xml"
            @change="handleFileActivity"
          >
        </div>
      </div>

      <!-- Uploaded File Preview -->
      <div
        v-if="iconActivity"
        class="border border-dashed border-purple-700 rounded-lg flex items-center justify-between px-4 py-2 mb-6 bg-purple-100"
      >
        <div class="flex items-center space-x-3">
          <img
            :src="iconActivity ? iconActivity.thumbnail || iconActivity.value : ''"
            class="w-6 h-6"
          >
          <div>
            <p class="text-sm font-medium">
              {{ iconActivity ? iconActivity.name || iconActivity.file.name : '-' }}
            </p>
            <p class="text-xs text-gray-500">
              {{ calculateFileSize() }}
            </p>
          </div>
        </div>
        <div
          class="self-center flex items-center justify-center icon-transparent-bg"
          @click="iconActivity = null"
        >
          <img :src="require('@/assets/shipo-icon-delete.svg')">
        </div>
      </div>

      <p class="text-purple-600 font-medium mb-2">
        Key Activity Status
      </p>
      <div>
        <span
          v-if="!form.statuses.length"
          class="text-danger text-sm absolute-bottom-5 left-0"
        >*Required</span>
        <data-table
          v-else
          :table-header="headerStatus"
          :table-data="sortedStatusData"
          :loading="isLoadingStatus || isLoading"
          body-height="60vh"
          @sort="() => {}"
        >
          <template #action="{ row, index }">
            <div class="flex flex-row gap-x-3">
              <div
                v-if="!isAdd"
                class="p-1 w-8 h-8 self-center flex items-center justify-center bg-secondary rounded cursor-pointer"
                @click="''"
              >
                <vs-icon
                  icon="add_circle_outline"
                  color="#fff"
                  size="22px"
                />
              </div>
              <div
                class="self-center flex items-center justify-center icon-transparent-bg"
                @click="onHandleDelete(row, index)"
              >
                <img :src="require('@/assets/shipo-icon-delete.svg')">
              </div>
            </div>
          </template>
          <template #order_level="{ row, index }">
            <div class="flex flex-row">
              <div
                class="mx-1 self-center flex items-center justify-center"
                :class="[index === 0 ? 'icon-disable-bg' : 'icon-transparent-bg']"
                @click="handleOrderIncrease(row, index)"
              >
                <img
                  :src="true
                    ? require('@/assets/shipo-icon-double-arrow-up.svg') : require('@/assets/shipo-icon-double-arrow-up.svg')"
                >
              </div>
              <div
                class="mx-1 self-center flex items-center justify-center"
                :class="[index === form.statuses.length-1 ? 'icon-disable-bg' : 'icon-transparent-bg']"
                @click="handleOrderDecrease(row, index)"
              >
                <img
                  :src="true
                    ? require('@/assets/shipo-icon-double-arrow-down.svg') : require('@/assets/shipo-icon-double-arrow-down.svg')"
                >
              </div>
            </div>
          </template>
          <template #code="{ row }">
            <div v-if="row.isEditing">
              <vs-input
                :color="!row.code ? 'danger' : ''"
                v-model="row.code"
                placeholder="Enter code"
              />
            </div>
            <div v-else>
              {{ row.code || '-' }}
            </div>
          </template>
          <template #name="{ row }">
            <div v-if="row.isEditing">
              <vs-input
                :color="!row.name ? 'danger' : ''"
                v-model="row.name"
                placeholder="Enter status name"
              />
            </div>
            <div
              v-else
              class="capitalize"
            >
              {{ row.name? row.name.toLowerCase() : '-' }}
            </div>
          </template>
          <template #name_bahasa="{ row }">
            <div v-if="row.isEditing">
              <vs-input
                :color="!row.name_bahasa ? 'danger' : ''"
                v-model="row.name_bahasa"
                placeholder="Enter name in bahasa"
              />
            </div>
            <div v-else>
              {{ row.name_bahasa || '-' }}
            </div>
          </template>
          <template #oee_id="{ row }">
            <template v-if="row.isEditing">
              <div class="relative w-56">
                <select-with-search
                  is-grouping
                  v-model="row.oee_id"
                  placeholder="OEE Activity (L2)"
                  :options="oeeOptions"
                  @input="(val) => onChangeOee(val, row)"
                />
              </div>
            </template>
            <template v-else>
              <div>
                {{ oeeLvl2Options.find(v => v.id === row.id).name }}
              </div>
            </template>
          </template>
          <template #oee_code="{ row }">
            {{ row.oee_code }}
          </template>
          <template #type="{ row }">
            {{ row.type }}
          </template>
          <template #visibility="{ row }">
            <template v-if="row.isEditing">
              <div class="relative w-56">
                <select-with-search
                  hide-search
                  v-model="row.visibility"
                  placeholder="Select visibility"
                  class="px-2"
                  :options="visibilityOptions"
                  :is-valid="!isEmpty(row.visibility)"
                />
              </div>
            </template>
            <template v-else>
              <div
                class="badge-trip-status"
              >
                {{ row.visibility }}
              </div>
            </template>
          </template>
          <template #icon="{ row, index }">
            <template v-if="row.isEditing">
              <div class="flex flex-row items-center">
                <input
                  type="file"
                  :id="`fileInput${index}`"
                  :ref="`fileInput${index}`"
                  class="hidden"
                  accept="image/png, image/svg+xml"
                  @change="handleFileStatus($event, row)"
                >
                <vs-button
                  class="bg-secondary text-white py-1 w-64 h-8"
                  :class="{'border-solid border-2 border-red-600': !row.icon}"
                  color="primary"
                  type="button"
                  @click="$refs[`fileInput${index}`].click()"
                >
                  <div class="flex flex-row items-center justify-start pr-2 w-full">
                    <img
                      :src="require('@/assets/shipo-icon-edit-upload.svg')"
                      class="flex-shrink-0 mr-2 h-6"
                    >
                    <div class="truncate">
                      {{ row.icon ? row.icon.name || row.icon.file.name : 'Select File' }}
                    </div>
                  </div>
                </vs-button>
              </div>
            </template>
            <template v-else>
              <img
                :src="row.icon ? row.icon.thumbnail :''"
                alt=""
                width="20"
              >
            </template>
          </template>
        </data-table>
      </div>
      
      <vs-button
        @click="addRow"
        class="button w-full font-medium mb-6"
        color="secondary"
        :disabled="isLoading"
      >
        + Add Key Activity Status
      </vs-button>
      
      <div class="flex justify-between mt-4">
        <vs-button
          type="border"
          color="primary"
          class="w-1/2 mr-2 button"
          @click="closeModal"
          :disabled="isLoading"
        >
          Cancel
        </vs-button>
        <vs-button
          class="w-1/2 button shadow-primary"
          color="primary"
          @click="onHandleSubmit"
          :disabled="isLoading"
        >
          {{ isAdd ? 'Add Key Activity' : 'Save Change' }}
        </vs-button>
      </div>
      <pop-up
        class-content="modal-confirm"
        :active.sync="popUp.isShown"
        is-warning
        title="Delete Key Activity Status"
        left-button="Cancel"
        right-button="Yes, Delete"
        @close="popUp = { isShown: false, index: null, tempName: '' }"
        @leftButton="popUp = { isShown: false, index: null, tempName: '' }"
        @rightButton="deleteAction"
        :z-index="99999"
      >
        <p class="text-center mt-1">
          Are you sure want to delete "{{ popUp.tempName }}"?
        </p>
        <p class="text-center">
          This action cannot be undone
        </p>
      </pop-up>
    </div>
  </vs-popup>
</template>

<script>
import { groupBy } from 'lodash'
import { mapGetters } from 'vuex'

import DataTable from '@/components/dataTable'
import PopUp from '@/components/popUp'
import SelectWithSearch from '@/components/selectWithSearch'
import { MAX_FILE_UPLOAD_SIZE_BYTES } from '@/fixtures/Constants'

export default {
  name: 'ModalKeyActivity',
  props: {
    isAdd: {
      type: Boolean,
      default: () => true
    },
    tripStatusData: {
      type: Object,
      default: () => {}
    },
    keyActivityData:  {
      type: Object,
      default: () => {}
    }
  },
  components: {
    DataTable,
    SelectWithSearch,
    PopUp
  },
  computed: {
    ...mapGetters({
      visibilityOptions: 'KeyActivityStatus/visibilityOptions',
      oeeLvl2Options: 'KeyActivityStatus/oeeLvl2Options'
    }),
    sortedStatusData() {
      return [...this.form.statuses].sort((a, b) => a.order_level - b.order_level)
    }
  },
  watch: {
    visibilityOptions(val) {
      if (val) {
        if (this.isAdd) {
          this.form.visibility = "B"
          return
        }
        this.form.visibility = this.keyActivityData?.visibility
      }
    },
    'form.code'(val) {
      if (val && val.includes(' ')) {
        this.form.code = val.replace(/\s+/g, '')
      }
    },
    tripStatusData: {
      handler(val) {
        if (val) {
          if (this.isAdd) {
            this.form.visibility = "B"
          }
          this.form.trip_status_id = val.id
          this.form.trip_status = val.name
          this.form.order_level = this.isAdd ? this.form.order_level : val.order_level
        }
      }
    },
    keyActivityData: {
      handler(val) {
        if (val) {
          if (this.isAdd) {
            this.form.order_level = val.order_level
            return
          }
          this.form.name = val.name
          this.form.name_bahasa = val.name_bahasa
          this.form.code = val.code
          this.form.visibility = val.visibility
          this.form.order_level = val.order_level
          this.iconActivity = val.icon

          this.fetchStatuses(val.id)
        }
      }
    },
    oeeLvl2Options: {
      handler(val) {
        const groupedOptions = groupBy(val, (l2)=> l2.parent.name)
        this.oeeOptions = Object.keys(groupedOptions).map(key => ({
          id: groupedOptions[key][0].parent_id,
          text: key,
          value: key,
          children: groupedOptions[key].map(go => ({
            id: go.id,
            text: go.original_name,
            value: go.id,
            type: go.type,
            code: go.code
          }))
        }))
      }
    },
    isLoading: {
      immediate: true,
      deep: true,
      handler(val) {
        if (val) {
          this.$vs.loading({
            type: 'radius',
            color: 'primary',
            container: '#modalContainer',
            scale: 0.5
          })

          return
        }

        this.$vs.loading.close('#modalContainer > .con-vs-loading')
      }
    }
  },
  data() {
    return {
      form: {
        trip_status_id: null,
        trip_status: null,
        name: null,
        name_bahasa: null,
        code: null,
        description: '',
        is_active: true,
        visibility: "B",
        order_level: null,
        statuses: []
      },
      iconActivity: null,
      isLoading: false,
      headerStatus: [
        { key: 'action', label: 'Action', sortable: false, width: '75px' },
        { key: 'order_level', label: 'Order Level', sortable: false, width: '75px' },
        { key: 'code', label: 'Status Code' },
        { key: 'name', label: 'Status Name (English)'},
        { key: 'name_bahasa', label: 'Status Name (Indonesia)' },
        { key: 'oee_id', label: 'OEE Activity (L2)' },
        { key: 'oee_code', label: 'OEE Code' },
        { key: 'type', label: 'Type' },
        { key: 'visibility', label: 'Visibility' },
        { key: 'icon', label: 'Icon' }
      ],
      oeeOptions: [],
      isLoadingStatus: false,
      popUp: { isShown: false, index: null, tempName: '' }
    }
  },
  methods: {
    calculateFileSize() {
      return this.iconActivity.size < 1024
        ? `${this.iconActivity.size  } Bytes`
        : this.iconActivity.size < 1048576
          ? `${(this.iconActivity.size / 1024).toFixed(2)  } KB`
          : `${(this.iconActivity.size / 1048576).toFixed(2)  } MB`
    },
    async fetchStatuses(id) {
      try {
        const data = await this.$store.dispatch('KeyActivityStatus/getActivityStatus', id)

        this.form.statuses = data.map((v) => {
          const selectedOee = this.oeeOptions?.flatMap(oeeData => oeeData.children)?.find(child => child.id === v.oee_id)
          const status = {
            ...v,
            oee_code: selectedOee?.code || '-',
            type: v.type ? v.type : selectedOee?.type || '-',
            isEditing: true
          }
          return status
        })
        this.form.statuses.sort((a, b) => a.order_level - b.order_level)
      } catch (error) {}
    },
    async handleFileActivity(event) {
      const file = event.target.files[0]
      if (!file) return

      const fileParts = file.name.split('.')
      const extension = fileParts.pop()
      const baseName = fileParts.join('.')

      // ✅ Rename: filename + timestamp + .ext
      const newFileName = `${baseName}_${Date.now()}.${extension}`
      const renamedFile = new File([file], newFileName, { type: file.type })

      if (renamedFile.size > MAX_FILE_UPLOAD_SIZE_BYTES) {
        this.showFailedNotification(this, 'File may not be greater than 5MB')
        event.target.value = '' // Reset value to trigger onchange listener
        return
      }

      const res = await this.validateIconActivity(renamedFile)
      if (!res.ok) {
        // show messages to user
        this.$vs.notify({ color: 'danger', title: 'Invalid file', text: res.errors.join('; ') })
        this.iconActivity = null
        e.target.value = ''
      } else {
        // ok
        const reader = new FileReader()
        reader.onload = (e) => {
          const base64 = e.target.result
          this.iconActivity = {
            value: base64,
            file: renamedFile,
            size: renamedFile.size
          }
        }
        reader.readAsDataURL(file)
      }
    },
    validateIconActivity(file) {
      return new Promise(async (resolve) => {
        const errors = []

        // basic checks
        const validTypes = ['image/png', 'image/svg+xml']
        if (!validTypes.includes(file.type)) {
          errors.push('Invalid file type. Only PNG or SVG allowed.')
          return resolve({ ok: false, errors })
        }

        return resolve({ ok: true })
      })
    },
    handleFileStatus(event, row) {
      const file = event.target.files[0]
      if (file) {
        const fileParts = file.name.split('.')
        const extension = fileParts.pop()
        const baseName = fileParts.join('.')

        // ✅ Rename: filename + timestamp + .ext
        const newFileName = `${baseName}_${Date.now()}.${extension}`
        const renamedFile = new File([file], newFileName, { type: file.type })

        if (renamedFile.size > MAX_FILE_UPLOAD_SIZE_BYTES) {
          this.showFailedNotification(this, 'File may not be greater than 5MB')
          event.target.value = '' // Reset value to trigger onchange listener
          return
        }

        const reader = new FileReader()
        reader.onload = (e) => {
          const base64 = e.target.result
          row.icon = {
            value: base64,
            file: renamedFile
          }
        }
        reader.readAsDataURL(renamedFile)
      }
    },
    addRow() {
      const defaultStatusForm = {
        name: null,
        name_bahasa: null,
        code: null,
        description: '',
        order_level: this.form.statuses.length + 1,
        visibility: "B",
        display_row_as: '',
        display_column_as: '',
        is_active: true,
        oee_id: null,
        oee_code: '-',
        type: '-',
        icon: null
      }

      this.form.statuses.push({
        isEditing: true,
        ...defaultStatusForm
      })
    },
    async onHandleSubmit() {
      try {
        this.isLoading = true
        // Populate payload data
        const statusIcons = []
        const payload = {
          ...this.form,
          statuses: this.form.statuses.map(status => {
            const { isEditing, icon, ...tempData } = status
            statusIcons.push({
              order_level: tempData.order_level,
              icon: icon
            })

            return tempData
          })
        }

        // Validate payload data
        const notRequired = [
          'order_level',
          'oee_id',
          'type',
          'description',
          'is_active',
          'display_row_as',
          'display_column_as',
          'oee_code',
          'oee_name',
          'statuses',
          'icon'
        ]

        const hasEmptyField = Object.keys(this.form)
          .find(key => !notRequired.includes(key) && (this.form[key] === '' || this.form[key] === null))

        if (hasEmptyField) {
          this.isLoading = false
          this.showFailedNotification(this, `Field ${hasEmptyField.replace('_', ' ')} cannot be empty`, 'Error')
          return
        }

        if (!this.form.statuses.length) {
          this.isLoading = false
          this.showFailedNotification(this, `Key activity status cannot be empty`, 'Error')
          return
        }

        const statusesHasEmptyField = this.form.statuses
          .some(v => Object.keys(v).some(
            key => !notRequired.includes(key) && (v[key] === '' || v[key] === null)
          ))

        if (statusesHasEmptyField) {
          this.isLoading = false
          this.showFailedNotification(this, `Some field in table key activity status cannot be empty`, 'Error')
          return
        }

        if (this.isEmpty(this.iconActivity)) {
          this.isLoading = false
          this.showFailedNotification(this, `Field key activity icon cannot be empty`, 'Error')
          return
        }

        if (statusIcons.some(v => !v.icon)) {
          this.isLoading = false
          this.showFailedNotification(this, `Field key activity status icon cannot be empty`, 'Error')
          return
        }

        if (this.isAdd) {
          // Handle POST Activity & Status
          const { data } = await this.$store.dispatch('KeyActivityStatus/postKeyActivity', payload)

          // Handle POST Upload Files
          const formData = new FormData()
          formData.append('file', this.iconActivity.file)
          formData.append('key_activity_id', data.id)
          statusIcons.forEach((v, i) => {
            formData.append(`statuses[${i}][file]`, v.icon.file)
            formData.append(`statuses[${i}][status_id]`, data.idStatus[i])
          })
          await this.$store.dispatch('KeyActivityStatus/uploadAttachment', formData)

          this.showSuccessNotification(this, 'Key Activity successfully added.')
        } else {
          const { trip_status_id, ...tempEditPayload } = payload

          const { data } = await this.$store.dispatch('KeyActivityStatus/putKeyActivity', {
            id: this.keyActivityData.id,
            form: {
              ...tempEditPayload,
              statuses: payload.statuses.map(status => {
                const { activity_id, oee_code, oee_name, icon, ...tempStatus } = status
                return tempStatus
              })
            }
          })

          const formData = new FormData()
          let isFileChange = false
          if (this.iconActivity?.file) {
            isFileChange = true
            formData.append('file', this.iconActivity.file)
          }
          formData.append('key_activity_id', this.keyActivityData.id)
          this.form.statuses.forEach((v, i) => {
            if (v.icon?.file) {
              isFileChange = true
              formData.append(`statuses[${i}][file]`, v.icon.file)
              formData.append(`statuses[${i}][status_id]`, data.statuses.find(d => v.order_level === d.order_level).id)
            }
          })
          if (isFileChange)
            await this.$store.dispatch('KeyActivityStatus/uploadAttachment', formData)
          
          this.showSuccessNotification(this, 'Key Activity successfully updated.')
        }

        this.closeModal()
      } catch ({ error }) {
        this.isLoading = false
        const message = error?.errors?.[0]?.[0]?.message || error?.message || 'Failed to save changes, please try again'

        this.showFailedNotification(this, message, 'Error')
      }
    },
    onChangeOee(value, row) {
      if (value && this.oeeOptions.length) {
        const selectedOee = this.oeeOptions.flatMap(v => v.children).find(c => c.id === value);

        if (selectedOee) {
          row.type = selectedOee.type
          row.oee_code = selectedOee.code
        }
      }
    },
    onHandleDelete(row, index) {
      this.popUp = { isShown: true, index: index, tempName: row.name }
    },
    deleteAction() {
      this.form.statuses.splice(this.popUp.index, 1)
      this.popUp = this.$options.data().popUp
    },
    handleOrderIncrease(row, index) {
      if (index === 0) return

      const selectedOrder = row.order_level
      const upperOrder = this.form.statuses[index - 1].order_level
      row.order_level = upperOrder
      this.form.statuses[index - 1].order_level = selectedOrder

      const temp = this.form.statuses[index]
      this.form.statuses.splice(index, 1)
      this.form.statuses.splice(index - 1, 0, temp)
    },
    handleOrderDecrease(row, index) {
      if (index === this.form.statuses.length-1) return

      const selectedOrder = row.order_level
      const lowerOrder = this.form.statuses[index + 1].order_level
      row.order_level = lowerOrder
      this.form.statuses[index + 1].order_level = selectedOrder

      const temp = this.form.statuses[index]
      this.form.statuses.splice(index, 1)
      this.form.statuses.splice(index + 1, 0, temp)
    },
    closeModal() {
      this.$emit('action')
      this.form = this.$options.data().form
      this.iconActivity = this.$options.data().iconActivity
      this.isLoading = false
    }
  }
}
</script>

<style lang="scss" scoped>
.popup {
  /deep/.vs-popup {
    @apply w-auto;
    @apply rounded-2xl;

    .modal-key-activity {
      width: 60rem;
    }

    .modal-confirm {
      width: 25rem;
    }

    &--title {
      @apply hidden;
    }

    &--content {
      @apply relative;
      @apply p-4;
      @apply m-0;
      @apply flex;
      @apply flex-col;
      @apply h-full;
      @apply w-full;
      @apply items-center;

      &-title {
        @apply text-2xl;
        @apply font-semibold;
        @apply text-grey;
        @apply mt-8;
      }
    }

    &--img {
      width: 152px;
    }

    &--close-button {
      @apply cursor-pointer;
      @apply absolute;
      width: 35px;
      top: 1rem;
      right: 1rem;
    }

    &--title-on-top {
      @apply absolute;
      width: 90%;
      top: 1.5rem;
      left: 1rem;
      font-weight: 600;
      font-size: 1.5rem;
    }

    &--ok {
      @apply rounded-xl;
      @apply border-2;
      @apply mt-20;
      @apply py-4;
      @apply min-w-button;
      @apply font-semibold;
      height: calc( 1rem * 10 / 3 );

      &.shadow-secondary {
        -webkit-box-shadow: 0 8px 25px -8px rgba(var(--vs-secondary),1);
        box-shadow: 0 8px 25px -8px rgba(var(--vs-secondary),1);
      }

      &:focus {
        @apply outline-none;
        @apply border-none;
      }
    }

    p {
      @apply text-base;
      @apply text-grey-001;
    }
  }
}

.icon-disable-bg {
  @apply cursor-pointer;
  @apply min-w-8;
  @apply h-8;
  border-radius: 0.5rem;

  background-color: #646464;
}
</style>
